import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { withTxToast } from '../utils/txToast';
import { Shield, Pause, Play, UserCog, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const AdminPanel = () => {
  const { web3, account, contract } = useWeb3();
  const { t } = useLanguage();
  const [isOwner, setIsOwner] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [newOwner, setNewOwner] = useState('');
  const [loading, setLoading] = useState({ pause: false, transfer: false });
  const [txHash, setTxHash] = useState({ pause: '', transfer: '' });
  const [error, setError] = useState({ pause: '', transfer: '' });

  useEffect(() => {
    checkOwnership();
  }, [contract, account]);

  const checkOwnership = async () => {
    if (!contract || !account) return;

    try {
      const owner = await contract.methods.owner().call();
      const paused = await contract.methods.paused().call();
      
      setIsOwner(owner.toLowerCase() === account.toLowerCase());
      setIsPaused(paused);
    } catch (err) {
      console.error('Error verificando ownership:', err);
    }
  };

  const handlePauseToggle = async () => {
    if (!contract || !account) return;

    setLoading({ ...loading, pause: true });
    setError({ ...error, pause: '' });
    setTxHash({ ...txHash, pause: '' });

    try {
      const method = isPaused ? 'unpause' : 'pause';
      const tx = await withTxToast(
        () => contract.methods[method]().send({ from: account, gas: 100000 }),
        isPaused ? t('admin.resumeToastLoading') : t('admin.pauseToastLoading'),
        isPaused ? t('admin.resumeToastSuccess') : t('admin.pauseToastSuccess')
      );

      setTxHash({ ...txHash, pause: tx.transactionHash });
      setIsPaused(!isPaused);
    } catch (err) {
      setError({ ...error, pause: err.message || t('admin.pauseToastError') });
    } finally {
      setLoading({ ...loading, pause: false });
    }
  };

  const handleTransferOwnership = async (e) => {
    e.preventDefault();
    
    if (!contract || !account) return;

    if (!web3.utils.isAddress(newOwner)) {
      setError({ ...error, transfer: t('admin.errInvalidAddress') });
      return;
    }

    setLoading({ ...loading, transfer: true });
    setError({ ...error, transfer: '' });
    setTxHash({ ...txHash, transfer: '' });

    try {
      const tx = await withTxToast(
        () => contract.methods.transferOwnership(newOwner).send({ from: account, gas: 100000 }),
        t('admin.ownershipToastLoading'),
        t('admin.ownershipToastSuccess')
      );

      setTxHash({ ...txHash, transfer: tx.transactionHash });
      setNewOwner('');
      setIsOwner(false);
    } catch (err) {
      setError({ ...error, transfer: err.message || t('admin.ownershipToastError') });
    } finally {
      setLoading({ ...loading, transfer: false });
    }
  };

  if (!account) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('admin.title')}</h2>
        </div>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('admin.noWallet')}
          </p>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('admin.title')}</h2>
        </div>
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            {t('admin.notOwner')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-6">
        <Shield className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">Panel de Administración</h2>
      </div>

      <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <p className="text-sm text-purple-800 font-medium">
          {t('admin.isOwner')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Pause/Unpause Section */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            {isPaused ? (
              <Play className="w-5 h-5 text-green-600" />
            ) : (
              <Pause className="w-5 h-5 text-orange-600" />
            )}
              <span>{t('admin.transfersTitle')}</span>
          </h3>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              {t('admin.currentStatus')} <span className={`font-semibold ${isPaused ? 'text-red-600' : 'text-green-600'}`}>
                {isPaused ? t('admin.paused') : t('admin.active')}
              </span>
            </p>
          </div>

          {error.pause && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error.pause}</p>
            </div>
          )}

          {txHash.pause && (
            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-800 font-medium">{t('admin.successTitle')}</p>
                <p className="text-xs text-green-700 mt-1 break-all">{t('admin.successTx', { hash: txHash.pause })}</p>
              </div>
            </div>
          )}

          <button
            onClick={handlePauseToggle}
            disabled={loading.pause}
            className={`w-full flex items-center justify-center space-x-2 font-semibold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              isPaused
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-orange-600 hover:bg-orange-700 text-white'
            }`}
          >
            {loading.pause ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>{t('transfer.processing')}</span>
              </>
            ) : isPaused ? (
              <>
                <Play className="w-5 h-5" />
                <span>{t('admin.resumeBtn')}</span>
              </>
            ) : (
              <>
                <Pause className="w-5 h-5" />
                <span>{t('admin.pauseBtn')}</span>
              </>
            )}
          </button>
        </div>

        {/* Transfer Ownership Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <UserCog className="w-5 h-5 text-blue-600" />
            <span>Transferir Ownership</span>
          </h3>

          <form onSubmit={handleTransferOwnership} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.newOwnerLabel')}
              </label>
              <input
                type="text"
                value={newOwner}
                onChange={(e) => setNewOwner(e.target.value)}
                placeholder={t('admin.newOwnerPlaceholder')}
                className="input-field"
                disabled={loading.transfer}
                required
              />
            </div>

            {error.transfer && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error.transfer}</p>
              </div>
            )}

            {txHash.transfer && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-green-800 font-medium">{t('admin.ownershipSuccessTitle')}</p>
                  <p className="text-xs text-green-700 mt-1 break-all">{t('admin.successTx', { hash: txHash.transfer })}</p>
                </div>
              </div>
            )}

            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-800">
                <strong>{t('admin.warningLabel')}</strong> {t('admin.warningText')}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading.transfer}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-center space-x-2"
            >
              {loading.transfer ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>{t('transfer.processing')}</span>
                </>
              ) : (
                <>
                  <UserCog className="w-5 h-5" />
                  <span>{t('admin.ownershipSubmit')}</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
